import { expertRulesPath } from "@/lib/businessRules";
import ProviderReferrals from "@/components/ProviderReferrals";
import { useEffect, useState, useMemo } from "react";
import api, { formatDkk } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Star, ExternalLink, Wallet, AlertTriangle, CheckCircle2, Inbox, Receipt, MessageSquareQuote } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import ExpertProfileForm, { expertFormValues } from "@/components/profile/ExpertProfileForm";
import ProfileAvatar from "@/components/profile/ProfileAvatar";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import ProfessionalQualifications from "@/components/profile/ProfessionalQualifications";
import DeleteAccountSection from "@/components/account/DeleteAccountSection";
const COMMISSION = 0.20;
const netOf = (p) => Math.round((p || 0) * (1 - COMMISSION));
const firstName = (s) => (s || "").split(" ")[0];

const VALID_TABS = ["overview", "requests", "earnings", "profile", "settings"];

export default function ExpertDashboard() {
  const { user, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestsError, setRequestsError] = useState("");
  const [profile, setProfile] = useState(user?.expert_profile || null);
  const [cats, setCats] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");
  const [exampleProfiles, setExampleProfiles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const urlTab = params.get("tab");
  const tab = VALID_TABS.includes(urlTab) ? urlTab : "overview";
  const setTab = (t) => {
    const nextParams = new URLSearchParams(params);
    if (!t || t === "overview") nextParams.delete("tab");
    else nextParams.set("tab", t);
    setParams(nextParams, { replace: false });
  };
  const [stripeState, setStripeState] = useState(null);
  const [stripeBusy, setStripeBusy] = useState(false);

  const loadStripe = () => {
    api.get("/experts/me/stripe/status").then((r) => setStripeState(r.data)).catch(() => {});
  };

  const startStripeOnboarding = async () => {
    setStripeBusy(true);
    try {
      const { data } = await api.post("/experts/me/stripe/onboarding-link");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || "Kunne ikke starte Stripe-opsætning");
    } finally {
      setStripeBusy(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const { data } = await api.post("/experts/me/stripe/login-link");
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      alert(e.response?.data?.detail || "Kunne ikke åbne Stripe-dashboard");
    }
  };

  const loadRequests = () => {
    setRequestsError("");
    return api.get("/expert/requests")
      .then((response) => setRequests(response.data || []))
      .catch(() => setRequestsError("Forespørgslerne kunne ikke indlæses."));
  };

  const reload = () => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
    loadRequests();
  };

  const loadCategories = () => {
    setCategoriesError("");
    api.get("/categories")
      .then((response) => setCats(response.data || []))
      .catch(() => setCategoriesError("Fagområderne kunne ikke indlæses."));
  };

  useEffect(() => {
    if (profile && !profile.approved && profile.approval_status !== "pending") {
      navigate("/expert/onboarding", { replace: true });
    }
  }, [navigate, profile]);

  useEffect(() => {
    let cancelled = false;
    const loadInitial = async () => {
      try {
        const [bookingResponse, exampleResponse] = await Promise.all([
          api.get("/bookings/mine"), api.get("/expert/example-profiles"),
        ]);
        if (cancelled) return;
        setBookings(bookingResponse.data || []);
        setExampleProfiles(exampleResponse.data || []);
        if (!profile && user?.expert_profile) setProfile(user.expert_profile);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadInitial();
    loadRequests();
    loadCategories();
    loadStripe();
    const t = setInterval(reload, 6000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    // fetch reviews for this expert once we know profile id
    if (!profile?.id) return;
    api.get(`/experts/${profile.id}`).then((r) => setReviews(r.data?.reviews || [])).catch(() => setReviews([]));
  }, [profile?.id]);

  const catNameById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c.name])), [cats]);

  const save = async (patch) => {
    await api.put("/expert/profile", patch);
    setProfile((p) => ({ ...p, ...patch }));
    refresh();
  };

  const completed = bookings.filter((b) => b.status === "completed");
  const pending = bookings.filter((b) => b.status !== "completed" && b.status !== "cancelled");
  const netLifetime = completed.reduce((s, b) => s + (b.expert_earnings_dkk || 0), 0);
  const netUpcoming = pending.reduce((s, b) => s + (b.expert_earnings_dkk || 0), 0);
  const now = new Date();
  const thisMonthNet = completed
    .filter((b) => {
      const d = new Date(b.created_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    })
    .reduce((s, b) => s + (b.expert_earnings_dkk || 0), 0);

  // real reviews (from actual bookings) vs demo reviews (from seed)
  const realReviews = reviews.filter((r) => !r.demo);
  const ratingCount = realReviews.length;
  const ratingAvg = ratingCount ? realReviews.reduce((s, r) => s + (r.rating || 0), 0) / ratingCount : null;
  const ratingDist = [5,4,3,2,1].map((n) => ({
    stars: n,
    count: realReviews.filter((r) => r.rating === n).length,
  }));
  const activeJobs = requests.filter((request) => request.expert_state === "selected");
  const incomingRequests = requests.filter((request) => request.expert_state !== "selected");

  if (loading) return <PageSkeleton cards={4}/>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
      {(tab === "overview" || tab === "earnings") && <ProviderReferrals />}
      {/* Header + KPIs only on the Dashboard (overview) tab */}
      {tab === "overview" && (
        <>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-brand-ink">Hej, {firstName(user?.name)}</h1>

            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            <Kpi label="Din indtjening" value={formatDkk(netLifetime)} sub="Netto lifetime" testid="kpi-earnings"/>
            <Kpi label="Denne måned" value={formatDkk(thisMonthNet)} sub={now.toLocaleString("da-DK",{month:"long"})} testid="kpi-month"/>
            <Kpi label="Til udbetaling" value={formatDkk(netUpcoming)} sub={`${pending.length} kommende`} testid="kpi-payout"/>
            <Kpi
              label="Gennemførte"
              value={String(profile?.consultations_count ?? completed.length)}
              sub={ratingCount ? `⭐ ${ratingAvg.toFixed(2)} · ${ratingCount} anmeldelser` : "Ingen anmeldelser endnu"}
              testid="kpi-consults"
              onClick={ratingCount ? () => { setTab("profile"); setTimeout(() => document.getElementById("expert-reviews-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 30); } : undefined}
            />
          </div>
        </>
      )}

      <p className="my-4 text-sm"><Link to={expertRulesPath} className="text-brand-green underline underline-offset-4">Regler og vilkår</Link></p>

      {/* Section title (mirrors top nav selection) */}
      <div className={tab === "overview" ? "mt-6 mb-3" : "mb-3"}>
        <h2 className="font-display text-lg font-semibold text-brand-ink">{sectionTitle(tab, requests.length)}</h2>
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <section className="mt-5 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-2.5">
            {activeJobs.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-[15px] font-semibold text-brand-ink">Aktive opgaver</h2>
                  <button onClick={() => setTab("requests")} className="text-[11px] text-brand-green hover:underline">Se alle →</button>
                </div>
                {activeJobs.slice(0, 2).map((p) => <RequestCard key={p.id} p={p} category={catNameById[p.category_id]} />)}
              </>
            )}
            <div className="flex items-center justify-between pt-1">
              <h2 className="font-display text-[15px] font-semibold text-brand-ink">Nye forespørgsler</h2>
              {incomingRequests.length > 0 && <button onClick={() => setTab("requests")} className="text-[11px] text-brand-green hover:underline">Se alle →</button>}
            </div>
            {incomingRequests.slice(0, 3).map((p) => <RequestCard key={p.id} p={p} category={catNameById[p.category_id]} />)}
            {incomingRequests.length === 0 && <div className="card-outline p-4 text-sm text-brand-muted">Ingen matchende forespørgsler lige nu.</div>}
          </div>
          <div className="space-y-2.5">
            <h2 className="font-display text-[15px] font-semibold text-brand-ink">Kommende</h2>
            {pending.slice(0, 6).map((b) => <UpcomingRow key={b.id} b={b}/>)}
            {pending.length === 0 && <div className="card-outline p-3 text-sm text-brand-muted">Ingen kommende konsultationer.</div>}
          </div>
        </section>
      )}

      {tab === "requests" && (
        <section className="mt-5" data-testid="expert-requests-section">
          {!profile?.approved && (
            <div className="mb-3 rounded-lg border border-[#F3D9C1] bg-[#FDF3EA] p-3 text-sm text-[#B0561D]">
              Din ekspertprofil er endnu ikke godkendt, så du kan ikke acceptere forespørgsler. En admin skal godkende din konto først.
            </div>
          )}
          {activeJobs.length > 0 && (
            <div className="mb-6" data-testid="expert-active-jobs">
              <h3 className="mb-2 font-display text-base font-semibold text-brand-ink">Aktive opgaver</h3>
              <div className="space-y-2.5">
                {activeJobs.map((p) => <RequestCard key={p.id} p={p} category={catNameById[p.category_id]} />)}
              </div>
            </div>
          )}
          <h3 className="mb-2 font-display text-base font-semibold text-brand-ink">Indgående forespørgsler</h3>
          {requestsError && <div className="mb-3 rounded-xl border border-[#B7791F] bg-[#FFF7E8] px-3 py-2 text-sm text-[#7A4B10]" role="alert">Forespørgslerne kunne ikke indlæses. <button type="button" onClick={loadRequests} className="font-semibold underline">Prøv igen</button></div>}
          <div className="space-y-2.5">
            {incomingRequests.map((p) => <RequestCard key={p.id} p={p} category={catNameById[p.category_id]} />)}
            {incomingRequests.length === 0 && !requestsError && (
              <EmptyState
                icon={<Inbox size={26} className="text-brand-green"/>}
                title="Ingen matchende forespørgsler lige nu"
                description="Vi matcher løbende nye kundeforespørgsler til din kategori og dine tags. Tjek igen om lidt."
                secondary={{ label: "Rediger min profil", to: "/expert?tab=profile", testid: "empty-requests-edit-profile" }}
                testid="empty-requests"
              />
            )}
          </div>
        </section>
      )}

      {tab === "earnings" && (
        <section className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-3 gap-2.5">
            <Kpi label="Netto lifetime" value={formatDkk(netLifetime)} sub={`${completed.length} afsluttet`}/>
            <Kpi label="Denne måned" value={formatDkk(thisMonthNet)} sub={now.toLocaleString("da-DK",{month:"long", year:"numeric"})}/>
            <Kpi label="Afventer udbetaling" value={formatDkk(netUpcoming)} sub={`${pending.length} kommende`}/>
          </div>

          <PayoutLedger onOpenStripe={openStripeDashboard}/>
          <div className="card-outline overflow-hidden">
            <div className="px-3.5 py-2 border-b border-brand-line text-[11px] font-mono uppercase tracking-widest text-brand-muted">Transaktioner</div>
            <div className="hidden md:grid grid-cols-[110px_1fr_120px_100px_100px_110px] gap-3 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-widest text-brand-muted border-b border-brand-soft">
              <div>Dato</div><div>Konsultation</div><div>Kunde betalte</div><div>Gebyr</div><div>Din netto</div><div className="text-right">Status</div>
            </div>
            <div className="divide-y divide-brand-soft">
              {completed.map((b) => (
                <div key={b.id} className="px-3.5 py-2.5 md:grid md:grid-cols-[110px_1fr_120px_100px_100px_110px] md:gap-3 md:items-center text-sm">
                  <div className="text-brand-body text-xs md:text-sm">{new Date(b.created_at).toLocaleDateString("da-DK")}</div>
                  <div className="text-brand-ink font-medium truncate">{b.problem_title || b.customer_name}</div>
                  <div className="hidden md:block font-mono text-brand-body">{formatDkk(b.price_dkk)}</div>
                  <div className="hidden md:block font-mono text-brand-muted">{formatDkk(b.commission_dkk)}</div>
                  <div className="hidden md:block font-mono font-semibold text-[#166534]">{formatDkk(b.expert_earnings_dkk)}</div>
                  <div className="md:text-right text-[11px] text-brand-green">Udbetales</div>
                  <div className="md:hidden text-[11px] text-brand-muted mt-1">
                    Kunde betalte {formatDkk(b.price_dkk)} · Gebyr {formatDkk(b.commission_dkk)} · <span className="font-medium text-[#166534]">Din netto {formatDkk(b.expert_earnings_dkk)}</span>
                  </div>
                </div>
              ))}
              {completed.length === 0 && (
                <div className="p-4">
                  <EmptyState
                    variant="compact"
                    icon={<Receipt size={20} className="text-brand-green"/>}
                    title="Ingen indtjening endnu"
                    description="Din første transaktion vises her, så snart en kunde betaler dig for en konsultation."
                    secondary={{ label: "Rediger prissætning", to: "/expert?tab=settings", testid: "empty-earnings-pricing" }}
                    testid="empty-earnings-transactions"
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {tab === "reviews" && (
        <section className="mt-5 hidden" data-testid="expert-reviews-section-legacy">{/* moved under Min profil */}</section>
      )}

      {tab === "profile" && profile && (
        <section className="mt-5 space-y-4" data-testid="expert-profile-section">
          <ProfileTab
            profile={profile}
            realReviews={realReviews}
            ratingAvg={ratingAvg}
            ratingCount={ratingCount}
            ratingDist={ratingDist}
            catNames={[...(profile.category_ids || [profile.category_id]).map((id) => catNameById[id]).filter(Boolean), ...(profile.custom_category ? [profile.custom_category] : [])]}
            save={save}
            cats={cats}
            categoriesError={categoriesError}
            onRetryCategories={loadCategories}
            exampleProfiles={exampleProfiles}
            catNameById={catNameById}
          />
        </section>
      )}

      {tab === "settings" && profile && (
        <section className="mt-5 space-y-4" data-testid="expert-settings-section">
          <SettingsTab
            profile={profile}
            save={save}
            stripeState={stripeState}
            stripeBusy={stripeBusy}
            onStripeStart={startStripeOnboarding}
            onStripeOpen={openStripeDashboard}
          />
        </section>
      )}

    </div>
  );
}

function PayoutLedger({ onOpenStripe }) {
  const [state, setState] = useState({ loading: true, activated: false, payouts: [], balance: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/experts/me/stripe/payouts");
        if (!cancelled) setState({ loading: false, ...data });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const fmt = (amt, cur) => `${(amt / 100).toLocaleString("da-DK", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${(cur || "dkk").toUpperCase()}`;
  const statusPill = (s) => {
    const map = {
      paid:       ["bg-brand-soft text-[#166534] border-brand-line-strong", "Udbetalt"],
      pending:    ["bg-[#FDF3EA] text-[#B0561D] border-[#F3D9C1]", "Afventer"],
      in_transit: ["bg-brand-soft text-brand-body border-brand-line", "På vej"],
      canceled:   ["bg-[#F7E4E4] text-[#991B1B] border-[#F3C1C1]", "Annulleret"],
      failed:     ["bg-[#F7E4E4] text-[#991B1B] border-[#F3C1C1]", "Mislykket"],
    };
    const [cls, label] = map[s] || ["bg-brand-soft text-brand-body border-brand-line", s || "—"];
    return <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${cls}`}>{label}</span>;
  };

  if (state.loading) {
    return <div className="card-outline p-3.5 text-sm text-brand-muted" data-testid="payout-ledger-loading">Henter udbetalinger…</div>;
  }
  if (!state.activated) {
    return (
      <div className="card-outline p-4 text-sm" data-testid="payout-ledger-inactive">
        <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Udbetalinger</div>
        <div className="mt-1 text-brand-body">Aktivér din Stripe-konto for at se udbetalinger her.</div>
      </div>
    );
  }
  const available = state.balance?.available?.[0];
  const pending = state.balance?.pending?.[0];
  return (
    <div className="card-outline overflow-hidden" data-testid="payout-ledger">
      <div className="px-3.5 py-2.5 border-b border-brand-line flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Stripe udbetalinger</div>
          <div className="mt-0.5 text-sm text-brand-ink">
            {available && <><span className="font-semibold text-[#166534]">{fmt(available.amount, available.currency)}</span> tilgængelig</>}
            {pending && <span className="text-brand-muted"> · {fmt(pending.amount, pending.currency)} på vej</span>}
          </div>
        </div>
        <button onClick={onOpenStripe} className="text-[11px] px-2.5 py-1 rounded-full border border-brand-green text-brand-green hover:bg-brand-wash" data-testid="ledger-open-stripe">
          Åbn Stripe →
        </button>
      </div>
      <div className="hidden md:grid grid-cols-[130px_130px_1fr_120px_100px] gap-3 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-widest text-brand-muted border-b border-brand-soft">
        <div>Ankommer</div><div>Oprettet</div><div>Beskrivelse</div><div>Beløb</div><div className="text-right">Status</div>
      </div>
      <div className="divide-y divide-brand-soft">
        {state.payouts.map((p) => {
          const arrival = p.arrival_date ? new Date(p.arrival_date * 1000) : null;
          const created = p.created ? new Date(p.created * 1000) : null;
          return (
            <div key={p.id} className="px-3.5 py-2.5 md:grid md:grid-cols-[130px_130px_1fr_120px_100px] md:gap-3 md:items-center text-sm" data-testid={`payout-row-${p.id}`}>
              <div className="text-brand-body text-xs md:text-sm">
                {arrival ? arrival.toLocaleDateString("da-DK", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </div>
              <div className="hidden md:block text-xs text-brand-muted">
                {created ? created.toLocaleDateString("da-DK", { day: "2-digit", month: "short" }) : "—"}
              </div>
              <div className="text-brand-ink truncate">{p.description || `Payout ${p.id.slice(-8)}`}</div>
              <div className="font-mono font-semibold text-[#166534]">{fmt(p.amount, p.currency)}</div>
              <div className="md:text-right mt-1 md:mt-0">{statusPill(p.status)}</div>
              <div className="md:hidden text-[11px] text-brand-muted mt-1">
                {created ? `Oprettet ${created.toLocaleDateString("da-DK")}` : ""}
              </div>
            </div>
          );
        })}
        {state.payouts.length === 0 && (
          <div className="px-3.5 py-4">
            <EmptyState
              variant="compact"
              icon={<Wallet size={20} className="text-brand-green"/>}
              title="Ingen udbetalinger endnu"
              description="Første udbetaling sker automatisk efter din første betalte konsultation. Beløb og ankomstdato vises her."
              testid="payout-ledger-empty"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function StripePayoutsCard({ state, busy, onStart, onOpen }) {  if (!state) {
    return (
      <div className="mt-4 card-outline p-3.5 text-sm text-brand-muted" data-testid="stripe-card-loading">Henter udbetalingsstatus…</div>
    );
  }
  if (state.onboarded) {
    return (
      <div className="mt-4 rounded-xl border border-brand-line-strong bg-brand-soft p-3.5 flex items-start gap-3" data-testid="stripe-card-active">
        <CheckCircle2 className="text-[#166534] mt-0.5 flex-shrink-0" size={18}/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-brand-ink">Udbetalinger aktive</div>
          <div className="text-[12px] text-brand-body">Dine indtjeninger sendes automatisk til din Stripe-konto.</div>
        </div>
        <button onClick={onOpen} className="text-xs px-3 py-1.5 rounded-full border border-brand-green text-brand-green hover:bg-white flex items-center gap-1 flex-shrink-0" data-testid="open-stripe-dashboard-btn">
          <Wallet size={12}/> Stripe-dashboard
        </button>
      </div>
    );
  }
  const inProgress = !!state.account_id;
  return (
    <div className="mt-4 rounded-xl border border-[#F3D9C1] bg-[#FDF3EA] p-3.5 flex items-start gap-3" data-testid="stripe-card-inactive">
      <AlertTriangle className="text-[#B0561D] mt-0.5 flex-shrink-0" size={18}/>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-brand-ink">
          {inProgress ? "Fuldfør din Stripe-opsætning" : "Aktivér udbetalinger"}
        </div>
        <div className="text-[12px] text-brand-body">
          Før kunder kan booke dig, skal du opsætte din Stripe-konto — det tager 2 minutter og bruges til at modtage dine udbetalinger.
        </div>
      </div>
      <button onClick={onStart} disabled={busy} className="text-xs px-3 py-1.5 rounded-full bg-brand-green text-white hover:bg-brand-hover disabled:opacity-50 flex items-center gap-1 flex-shrink-0" data-testid="start-stripe-onboarding-btn">
        <Wallet size={12}/> {busy ? "…" : (inProgress ? "Fortsæt opsætning" : "Opsæt Stripe")}
      </button>
    </div>
  );
}

function RequestCard({ p, category }) {
  const selected = p.expert_state === "selected";
  const when = ["asap", "ready_now"].includes(p.scheduling_status) || (!p.scheduling_status && p.selected_availability_type === "asap")
    ? "Så hurtigt som muligt"
    : (p.scheduled_at || p.selected_appointment_time)
      ? new Intl.DateTimeFormat("da-DK", { weekday:"short", day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }).format(new Date(p.scheduled_at || p.selected_appointment_time))
      : p.scheduling_status === "proposed" ? "Nyt tidspunkt afventer svar" : "Tidspunkt afventer";
  const price = p.accepted_proposal?.price_model === "hourly"
    ? `${formatDkk(p.accepted_proposal.hourly_rate)}/time`
    : p.accepted_proposal?.fixed_price ? formatDkk(p.accepted_proposal.fixed_price) : null;
  return (
    <article className={`card-outline overflow-hidden p-4 transition-shadow hover:shadow-md ${selected ? "border-brand-line-strong bg-[#FCFFFD]" : ""}`} data-testid={`request-${p.id}`}>
      <div className="flex gap-4">
        {p.customer_profile ? <ProfileAvatar photoUrl={p.customer_profile.photo_url} name={p.customer_profile.name} size="md" className="shrink-0"/> : p.media?.[0]?.url ? <img src={p.media[0].url} alt="" className="h-16 w-16 shrink-0 rounded-xl object-cover"/> : <ProfileAvatar name={p.customer_name || "Kunde"} size="md" className="shrink-0"/>}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3"><div><h3 className="font-display text-base font-semibold text-brand-ink">{p.title}</h3><div className="mt-1 flex flex-wrap gap-x-2 text-xs text-brand-muted">{category && <span>{category}</span>}<span>{p.customer_profile?.name || p.customer_name || "Kunde"}</span>{p.media?.length>0&&<span>{p.media.length} vedhæftning{p.media.length===1?"":"er"}</span>}</div></div><span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${selected?"bg-[#E5F2EA] text-brand-fresh":p.help_timing==="now"?"bg-[#FFF0E2] text-[#A64B17]":"bg-brand-soft text-brand-body"}`}>{selected?"Aktiv opgave":p.help_timing==="now"?"Haster":"Ny forespørgsel"}</span></div>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-brand-body">{p.description}</p>
          {selected && <div className="mt-3 grid gap-2 rounded-xl bg-brand-soft p-3 text-xs sm:grid-cols-3"><div><span className="block text-[10px] uppercase text-brand-muted">Pris</span><strong>{price || "Aftalt i prisforslag"}</strong></div><div><span className="block text-[10px] uppercase text-brand-muted">Tidspunkt</span><strong>{when}</strong></div><div><span className="block text-[10px] uppercase text-brand-muted">Status</span><strong>Kunden har valgt dig</strong></div></div>}
          <div className="mt-3 flex items-center justify-between"><span className="inline-flex items-center gap-1 text-[11px] text-brand-muted"><MessageSquareQuote size={13}/>Seneste aktivitet {p.updated_at ? new Date(p.updated_at).toLocaleDateString("da-DK") : "i dag"}</span><Link to={`/expert/forespoergsel/${p.id}`} className="inline-flex min-h-9 items-center rounded-lg bg-brand-green px-4 text-xs font-semibold text-white">{selected?"Åbn opgave":"Se opgave"}</Link></div>
        </div>
      </div>
    </article>
  );
}

function sectionTitle(tab, requestCount) {
  const map = {
    overview: "Dashboard",
    requests: `Forespørgsler${requestCount ? ` · ${requestCount}` : ""}`,
    earnings: "Indtjening",
    profile: "Min profil",
    settings: "Indstillinger",
  };
  return map[tab] || "Dashboard";
}

function ProfileTab({ profile, realReviews, ratingAvg, ratingCount, ratingDist, catNames, save, cats, categoriesError, onRetryCategories, exampleProfiles, catNameById }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const initialValues = useMemo(() => expertFormValues(user, profile), [user, profile]);
  const profileStrengthChecks = [
    { label: "Skriv en konkret hjælpbeskrivelse", complete: String(profile.help_summary || "").trim().length >= 120 },
    { label: "Uddyb din erfaring i Om mig", complete: String(profile.bio || "").trim().length >= 250 },
    { label: "Tilføj mindst 3 specialer", complete: (profile.specialties || []).length >= 3 },
    { label: "Angiv konkrete tider du typisk er ledig", complete: Boolean(profile.availability_note && profile.availability_note !== "Efter aftale") },
    { label: "Tilføj en verificeret kvalifikation", complete: (profile.professional_badges || []).length > 0 },
  ];
  const profileStrength = Math.round(
    (profileStrengthChecks.filter((item) => item.complete).length / profileStrengthChecks.length) * 100,
  );

  const saveFullProfile = async (payload) => {
    const { full_name, ...profileUpdate } = payload;
    await api.put("/users/me/profile", {
      name: full_name,
      photo_url: payload.photo_url,
      city: user?.city || "",
      language: payload.languages[0] || user?.language || "Dansk",
    });
    await save(profileUpdate);
    setEditing(false);
  };
  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12px] text-brand-muted">Sådan ser kunderne dig. Redigér for at opdatere din offentlige profil.</p>
        <div className="flex items-center gap-2 flex-wrap">
          {profile?.id && (
            <Link
              to={`/eksperter/${profile.id}`}
              target="_blank"
              rel="noreferrer"
              data-testid="view-public-profile-cta"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-brand-line text-brand-ink hover:bg-brand-soft"
            >
              <ExternalLink size={12}/> Se min offentlige profil
            </Link>
          )}
          <button
            onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-brand-green text-white hover:bg-brand-hover"
            data-testid="edit-profile-toggle"
          >
            {editing ? "Færdig" : "Rediger profil"}
          </button>
        </div>
      </div>

      <div className="card-outline p-4 sm:p-5" data-testid="profile-strength">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-semibold text-brand-ink">Gør din profil stærkere</h3>
            <p className="mt-1 text-xs text-brand-muted">En konkret profil gør det lettere for kunderne at vælge dig.</p>
          </div>
          <span className="font-display text-xl font-bold text-brand-green">{profileStrength}%</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-brand-line" aria-label={`Profilstyrke ${profileStrength} procent`}>
          <div className="h-full rounded-full bg-brand-fresh transition-[width]" style={{ width: `${profileStrength}%` }}/>
        </div>
        {profileStrength < 100 && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {profileStrengthChecks.filter((item) => !item.complete).map((item) => (
              <div key={item.label} className="flex items-start gap-2 text-xs text-brand-body">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-fresh"/>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {!editing ? (
        <div className="card-outline p-5" data-testid="profile-preview">
          <div className="flex items-start gap-4 flex-wrap">
            <ProfileAvatar photoUrl={profile.photo_url} name={user?.name} size="lg" className="!w-20 !h-20"/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-xl font-bold text-brand-ink">{profile.name || profile.headline?.split("–")[0]?.trim() || "Ekspert"}</h3>
                {profile.approved && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-soft text-brand-green border border-brand-line-strong">Godkendt</span>}
              </div>
              {profile.headline && <div className="mt-1 text-sm text-brand-ink">{profile.headline}</div>}
              <div className="mt-1 text-[11px] text-brand-muted flex items-center gap-2 flex-wrap">
                {catNames?.length > 0 && <span>{catNames.join(" · ")}</span>}
                {(profile.years_experience || 0) > 0 && <><span>·</span><span>{profile.years_experience} års erfaring</span></>}
                {(profile.languages || []).length > 0 && <><span>·</span><span>{(profile.languages || []).join(", ")}</span></>}
                {ratingCount > 0 && ratingAvg != null && <><span>·</span><span className="inline-flex items-center gap-0.5 text-[#D97706]"><Star size={11} className="fill-current"/>{ratingAvg.toFixed(2)} ({ratingCount})</span></>}
              </div>
            </div>
          </div>

          {profile.help_summary && (
            <div className="mt-4 rounded-xl bg-brand-soft p-4">
              <div className="font-mono uppercase text-[10px] tracking-widest text-brand-fresh">Sådan kan jeg hjælpe</div>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#35534A]">{profile.help_summary}</p>
            </div>
          )}

          {profile.bio && (
            <div className="mt-4">
              <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Om mig</div>
              <p className="mt-1 text-sm text-brand-body whitespace-pre-wrap leading-relaxed">{profile.bio}</p>
            </div>
          )}

          {(profile.specialties || []).length > 0 && (
            <div className="mt-4">
              <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Specialer</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(profile.specialties || []).map((s) => <span key={s} className="chip">{s}</span>)}
              </div>
            </div>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-brand-soft p-3 text-sm text-brand-body">
              <div className="text-[10px] uppercase tracking-widest text-brand-muted">Tilgængelighed</div>
              <div className="mt-1">{profile.availability_note || "Ikke angivet"}</div>
            </div>
            <div className="rounded-xl bg-brand-soft p-3 text-sm text-brand-body">
              <div className="text-[10px] uppercase tracking-widest text-brand-muted">Pris</div>
              <div className="mt-1">Minimum {profile.price_min ? formatDkk(profile.price_min) : "ikke angivet"} · {profile.hourly_rate ? `${formatDkk(profile.hourly_rate)} / time` : "timepris ikke angivet"}</div>
            </div>
          </div>
        </div>
      ) : (
        <div data-testid="profile-edit">
          <ExpertProfileForm
            initialValues={initialValues}
            categories={cats}
            categoriesError={categoriesError}
            onRetryCategories={onRetryCategories}
            onSubmit={saveFullProfile}
            submitLabel="Gem profil"
            testId="expert-profile-edit-form"
          />
        </div>
      )}

      <div id="expert-reviews-anchor" data-testid="expert-reviews-section">
        <h3 className="font-display text-[15px] font-semibold text-brand-ink mb-2">Anmeldelser</h3>
        {ratingCount === 0 ? (
          <div className="card-outline p-5 text-sm text-brand-muted">Du har endnu ingen anmeldelser. De vises her når kunder giver feedback efter en konsultation.</div>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-4">            <div className="card-outline p-4 h-fit">
              <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Samlet</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold text-brand-ink">{ratingAvg.toFixed(2)}</span>
                <span className="text-xs text-brand-muted">/ 5</span>
              </div>
              <div className="text-xs text-brand-muted mt-0.5">{ratingCount} anmeldelser</div>
              <div className="mt-3 space-y-1">
                {ratingDist.map((d) => {
                  const pct = ratingCount ? Math.round((d.count / ratingCount) * 100) : 0;
                  return (
                    <div key={d.stars} className="flex items-center gap-2 text-[11px]">
                      <span className="w-4 text-brand-body">{d.stars}</span>
                      <Star size={10} className="fill-[#D97706] text-[#D97706]"/>
                      <div className="flex-1 h-1.5 rounded-full bg-brand-soft overflow-hidden">
                        <div className="h-full bg-brand-green" style={{ width: `${pct}%` }}/>
                      </div>
                      <span className="w-6 text-right text-brand-muted">{d.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              {realReviews.map((r) => (
                <div key={r.id} className="card-outline p-3.5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1 text-[#D97706] text-sm">
                      {[1,2,3,4,5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-current" : "text-brand-line"}/>)}
                    </div>
                    <div className="text-[11px] text-brand-muted">{firstName(r.customer_name)} · {new Date(r.created_at).toLocaleDateString("da-DK")}</div>
                  </div>
                  {r.comment && <p className="mt-1.5 text-sm text-brand-ink leading-relaxed">{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {exampleProfiles.length > 0 && (
        <div data-testid="expert-example-profiles">
          <div className="mb-3">
            <h3 className="font-display text-[15px] font-semibold text-brand-ink">Inspiration til din profil</h3>
            <p className="mt-1 text-xs text-brand-muted">Disse profiler er kun eksempler og kan ikke bookes af kunder.</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {exampleProfiles.map((example) => (
              <div key={example.id} className="card-outline p-4" data-testid={`expert-example-profile-${example.id}`}>
                <div className="flex items-start gap-3">
                  <ProfileAvatar photoUrl={example.photo_url} name={example.name} size="md"/>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-semibold text-brand-ink">{example.name}</h4>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFF4D6] text-[#7A5200] border border-[#E8D39A]">Eksempelprofil</span>
                    </div>
                    {example.headline && <p className="mt-1 text-sm text-brand-body">{example.headline}</p>}
                    {catNameById[example.category_id] && <p className="mt-1 text-[11px] text-brand-muted">{catNameById[example.category_id]}</p>}
                  </div>
                </div>
                {example.bio && <p className="mt-3 text-xs leading-relaxed text-brand-body line-clamp-4">{example.bio}</p>}
                {(example.specialties || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {example.specialties.slice(0, 5).map((specialty) => <span key={specialty} className="chip">{specialty}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function SettingsTab({ profile, save, stripeState, stripeBusy, onStripeStart, onStripeOpen }) {
  return (
    <>
      <ProfessionalQualifications/>
      <div className="card-outline p-4" data-testid="settings-payouts">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">Udbetalinger (Stripe)</div>
            <p className="mt-1 text-[12px] text-brand-body">
              {stripeState?.onboarded
                ? "Din Stripe-konto er aktiv. Udbetalinger sendes automatisk."
                : "Aktivér din Stripe-konto for at kunne modtage betalinger for konsultationer."}
            </p>
          </div>
          {stripeState?.onboarded ? (
            <button onClick={onStripeOpen} className="text-xs px-3 py-1.5 rounded-full border border-brand-green text-brand-green hover:bg-brand-wash flex items-center gap-1" data-testid="open-stripe-dashboard-btn">
              <Wallet size={12}/> Åbn Stripe-dashboard
            </button>
          ) : (
            <button onClick={onStripeStart} disabled={stripeBusy} className="text-xs px-3 py-1.5 rounded-full bg-brand-green text-white hover:bg-brand-hover disabled:opacity-50 flex items-center gap-1" data-testid="start-stripe-onboarding-btn">
              <Wallet size={12}/> {stripeBusy ? "…" : (stripeState?.account_id ? "Fortsæt opsætning" : "Opsæt Stripe")}
            </button>
          )}
        </div>
      </div>
      <DeleteAccountSection/>
    </>
  );
}

function UpcomingRow({ b }) {
  const target = b.problem_id ? `/expert/forespoergsel/${b.problem_id}` : `/konsultation/${b.id}`;
  return (
    <Link to={target} className="card-outline p-2.5 flex items-center justify-between gap-2 hover-lift" data-testid={`expert-upcoming-${b.id}`}>
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-brand-ink truncate">{b.problem_title || b.customer_name}</div>
        <div className="text-[10px] text-brand-muted">{new Date(b.created_at).toLocaleString("da-DK")} · {b.duration_min} min</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="font-mono text-[11px] text-[#166534]">{formatDkk(b.expert_earnings_dkk)}</span>
        <ChevronRight size={13} className="text-brand-muted"/>
      </div>
    </Link>
  );
}

function Kpi({ label, value, sub, testid, onClick, icon }) {
  const clickable = !!onClick;
  return (
    <button
      onClick={onClick}
      disabled={!clickable}
      className={`card-outline p-3 text-left ${clickable ? "cursor-pointer hover:border-brand-green transition-colors" : ""}`}
      data-testid={testid}
    >
      <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        {icon}
        <span className="font-display text-lg font-bold text-brand-ink">{value}</span>
      </div>
      {sub && <div className="text-[10px] text-brand-muted mt-0.5">{sub}</div>}
    </button>
  );
}

function Field({ label, value, onSave, textarea }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  return (
    <div>
      <div className="font-mono uppercase text-[10px] tracking-widest text-brand-muted">{label}</div>
      {textarea ? (
        <textarea value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onSave(v)} rows={3} className="mt-1 card-outline px-3 py-2 w-full resize-none text-sm"/>
      ) : (
        <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onSave(v)} className="mt-1 card-outline px-3 py-2 w-full text-sm"/>
      )}
    </div>
  );
}

